import {
  Button,
  FormField,
  FormGrid,
  Page,
  SelectionList,
  Text,
} from '@dixa/lib-shared-habla';
import { useCallback, useMemo } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';

import audioManager, { NotificationSoundTypeT } from 'app/utils/audioManager';
import { HookFormSelectionListSingle } from 'components/hookForm';
import {
  NOTIFICATION_DEFAULT,
  NOTIFICATION_SOUND_OPTIONS,
  OFFER_NOTIFICATION_INCOMING,
  OFFER_NOTIFICATION_SOUND_OPTIONS,
} from 'components/notifications/sagas/audio';
import { actions as personalActions } from 'components/settings/actions/personal';
import { NotificationType, useNotifications } from 'hooks/useNotifications';
import { useBreakpointValue } from 'utils/mediaQuery';
import { useAppDispatch } from 'utils/redux/useAppDispatch';
import { useAppSelector } from 'utils/redux/useAppSelector';

export type PreferencesSoundFormFieldValues = {
  offer_notification_phone: string;
  offer_notification_chat: string;
  offer_notification_email: string;
  offer_notification_messaging: string;
  notification_sound: string;
};

export const PreferencesSoundForm = (): JSX.Element => {
  const isSmallScreen = useBreakpointValue({ base: true, md: false });
  const notify = useNotifications();
  const dispatch = useAppDispatch();
  const soundsState = useAppSelector((state) => state.settings.personal.sounds);
  const form = useForm<PreferencesSoundFormFieldValues>({
    mode: 'all',
    defaultValues: {
      offer_notification_phone:
        soundsState?.pstnphone?.tone ?? OFFER_NOTIFICATION_INCOMING,
      offer_notification_chat:
        soundsState?.widgetchat?.tone ?? OFFER_NOTIFICATION_INCOMING,
      offer_notification_email:
        soundsState?.email?.tone ?? OFFER_NOTIFICATION_INCOMING,
      offer_notification_messaging:
        soundsState?.messaging?.tone ?? OFFER_NOTIFICATION_INCOMING,
      notification_sound:
        soundsState?.notification?.tone ?? NOTIFICATION_DEFAULT,
    },
  });
  const { control, formState, getValues, handleSubmit } = form;

  const handlePlaySound = useCallback(
    (name: string) => {
      const values = getValues();
      audioManager.stop();
      audioManager.play(values[name], name as NotificationSoundTypeT, false);
    },
    [getValues],
  );

  const offerSoundsFormFields = useMemo(
    (): JSX.Element[] =>
      Object.entries(offerSoundsAssets).map(([name, label]) => (
        <FormField key={name} span={isSmallScreen ? 6 : 3}>
          <HookFormSelectionListSingle
            control={control}
            name={name as keyof PreferencesSoundFormFieldValues}
            label={label}
            rules={{
              required: 'Please select a sound',
            }}
            componentProps={{
              placeholder: 'Select a sound',
              suffix: {
                type: 'button',
                button: {
                  icon: 'volume-up',
                  onClick: (e) => {
                    e.stopPropagation();
                    handlePlaySound(name);
                  },
                  'aria-label': `Play ${label}`,
                },
              },
              getItemTitleByValue: (value) =>
                OFFER_NOTIFICATION_SOUND_OPTIONS.find(
                  (item) => item.value === value,
                )?.label ?? value,
            }}
          >
            {OFFER_NOTIFICATION_SOUND_OPTIONS.map(({ label, value }) => (
              <SelectionList.Item key={value} value={value}>
                <Text.Paragraph>{label}</Text.Paragraph>
              </SelectionList.Item>
            ))}
          </HookFormSelectionListSingle>
        </FormField>
      )),
    [control, handlePlaySound, isSmallScreen],
  );

  const onSubmit: SubmitHandler<PreferencesSoundFormFieldValues> = (values) => {
    dispatch(
      personalActions.personalSound({
        key: 'sounds',
        value: {
          pstnphone: {
            tone: values.offer_notification_phone,
          },
          widgetchat: {
            tone: values.offer_notification_chat,
          },
          email: {
            tone: values.offer_notification_email,
          },
          messaging: {
            tone: values.offer_notification_messaging,
          },
          notification: {
            tone: values.notification_sound,
          },
        },
      }),
    );
    notify(
      {
        title: 'Success',
        text: `Preferences have been updated.`,
        icon: 'done',
        time: 1500,
      },
      NotificationType.Success,
    );
    form.reset(values);
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Page.Section
        title="Ringtones"
        controls={
          <Button
            type="submit"
            isLoading={formState.isSubmitting}
            disabled={
              !formState.isValid || formState.isSubmitting || !formState.isDirty
            }
          >
            Save changes
          </Button>
        }
      >
        <FormGrid>{offerSoundsFormFields}</FormGrid>
      </Page.Section>
      <Page.Section title="Sound notifications">
        <FormGrid>
          <FormField span={isSmallScreen ? 6 : 3}>
            <HookFormSelectionListSingle
              control={control}
              name="notification_sound"
              label="Notification sound"
              rules={{
                required: 'Please select a sound',
              }}
              componentProps={{
                placeholder: 'Select a sound',
                suffix: {
                  type: 'button',
                  button: {
                    icon: 'volume-up',
                    onClick: (e) => {
                      e.stopPropagation();
                      handlePlaySound('notification_sound');
                    },
                    'aria-label': `Play notification sound`,
                  },
                },
                getItemTitleByValue: (value) =>
                  NOTIFICATION_SOUND_OPTIONS.find(
                    (item) => item.value === value,
                  )?.label ?? value,
              }}
            >
              {NOTIFICATION_SOUND_OPTIONS.map(({ label, value }) => (
                <SelectionList.Item key={value} value={value}>
                  <Text.Paragraph>{label}</Text.Paragraph>
                </SelectionList.Item>
              ))}
            </HookFormSelectionListSingle>
          </FormField>
        </FormGrid>
      </Page.Section>
    </form>
  );
};

const offerSoundsAssets: Record<string, string> = {
  offer_notification_phone: 'Phone sound',
  offer_notification_chat: 'Chat sound',
  offer_notification_email: 'Email sound',
  offer_notification_messaging: 'Messaging sound',
};
